// src/app/argent/resas.js — les réservations de l'écran Argent : saisie,
// montants et ménage manuels, suppression, restauration, déplacement de mois,
// prorata.
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { uid } from '../../lib/format'
import { applyManualOriginFlag, pushStableCodeTombstone, removeStableCodeTombstone } from '../../lib/resa-tombstones'
// Relecture E : « Restaurer » refusé si le même code vit dans une autre chambre.
import { chambreOuLeCodeVit, texteRestaurationRefusee } from '../../lib/csv-codes-absents'
import { chambresDuGroupeBooking } from '../../lib/csv-bascule'
import { removeMonthOverride, setMonthOverride } from '../../lib/resa-month-overrides'
import { computeMonthsTouched, assertProrataPosable, setProrataSplitStore, removeProrataSplitStore } from '../../lib/resa-prorata-splits'
import { findResaBucket, canEditMonth, canCreateMonth, computeInvoiceMonthByNights, addMonthsMk, canMoveTarget, resaIdentityKey, moveResaMonth, isRelocatable } from '../../lib/resa-invoice-month'
import { collectMontantOverrides } from '../../lib/data-projection'
import { getResasForMonth } from '../../lib/resas-v2-source'
import { findBlockingCommission, findBlockingVoyageurInvoice, findBlockingVoyageurInvoiceForMonth } from '../../lib/invoice-guard'

export function resasArgent({
  data, invoicesRaw, month, setMonth, setConfirm, setResaModal, y, m, saveData, v2Lids,
  resasByLogement, getLogById, monthKeyToLabel, dataProjete,
}) {
  // WAGON M (FIX MINEUR-1) : lecture sur `dataProjete` (= `data` tant qu'aucun
  // logement n'est 'v2', donc byte-identique en prod). Un logement basculé 'v2'
  // SANS résa KV n'apparaissait jamais dans le badge « factures en attente »
  // (hasData lisait `data` brut) → désormais il voit les résas projetées.
  const hasData = lid => { const d=dataProjete?.[month]?.[lid]; return d&&d.reservations&&d.reservations.length>0 }

  // Détection "résa incomplète" : montant à 0 (peu importe le voyageur).
  // Règle volontairement relâchée : même une résa nommée mais sans montant
  // bloque la facturation, donc doit être signalée. Ex : 09/04 Sylvie /
  // Booking 7971207 → nom renseigné, montant 0€.
  const isResaIncomplete = r => (Number(r.montant) || 0) === 0
  const countIncompletesLog = lid =>
    (dataProjete?.[month]?.[lid]?.reservations || []).filter(Boolean).filter(isResaIncomplete).length
  const countIncompletesLogWithChambres = log =>
    countIncompletesLog(log.id) + (log.chambres || []).reduce((s, c) => s + countIncompletesLog(c.id), 0)
  const navM = dir => { const d=new Date(y,m-1+dir); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`) }

  const ensureEntry = (nd,lid) => {
    if (!nd[month]) nd[month]={}
    if (!nd[month][lid]) nd[month][lid]={reservations:[],extras:[]}
    if (!nd[month][lid].reservations) nd[month][lid].reservations=[]
    if (!nd[month][lid].extras) nd[month][lid].extras=[]
  }
  // Fix iCal 2026-05-23 (plan-fix-ical-respect-modifications-manuelles.md) :
  // saveResa pose `manual_origin: true` systématiquement + préserve ical_uid /
  // ical_sync_log. deleteResa pose tombstones multi-canaux (UID iCal, date
  // legacy B-1, airbnbCode B-3, bookingRef B-3). Helpers extraits dans
  // lib/resa-tombstones.js pour testabilité.
  const saveResa = (lid,resa) => {
    const nd=JSON.parse(JSON.stringify(data))
    // Plan B (ADR-182) : à l'ÉDITION, on édite la résa LÀ où elle vit (son
    // bucket physique peut différer du mois affiché si elle a été relocalisée
    // via ◀▶, month_override). À la CRÉATION, elle va dans le mois courant,
    // mais le plancher canCreateMonth interdit toute création ≤ avril 2026.
    const loc = resa.id ? findResaBucket(nd, lid, resa.id) : null
    if (loc) {
      // Plan B (ADR-182) — garde plancher édition : avril autorisé (corrections
      // ponctuelles), mais mars 2026 et avant sont verrouillés (factures émises).
      if (!canEditMonth(loc.mk)) {
        alert("Édition impossible : mois ≤ mars 2026 verrouillé (factures émises).")
        return
      }
      const existing = nd[loc.mk][lid].reservations[loc.idx]
      nd[loc.mk][lid].reservations[loc.idx] = applyManualOriginFlag(resa, existing)
    } else {
      if (!canCreateMonth(month)) {
        alert("Création impossible : mois minimum mai 2026 (plancher facturation).")
        return
      }
      ensureEntry(nd,lid)
      nd[month][lid].reservations.push({ ...applyManualOriginFlag(resa, null), id: uid() })
    }
    saveData(nd); setResaModal(null)
  }
  // WAGON M (M3.5) — override ménage manuel pour une résa V2 (logement 'v2'
  // uniquement). On écrit dans `data` BRUT à data[mk][lid].menageOverrides[code],
  // JAMAIS dans les .reservations projetées (la projection relit l'override au
  // render suivant via applyMenageOverrides). `code` = clé stable
  // (airbnbCode || bookingRef). Une résa SANS code stable n'est PAS éditable
  // (décision pessimist F-M3-5 — jamais de clé fallback destructive).
  //   - value = nombre fini → pose/écrase l'entrée.
  //   - value = null/'' → SUPPRIME l'entrée (retour au forfait par défaut), on
  //     n'écrit jamais le forfait en dur.
  // Awaité (pattern saveResa) + erreur visible. mk = mois affiché (les résas
  // rendues viennent de dataProjete[month][lid]).
  const saveMenageOverride = async (lid, resa, value) => {
    const code = (resa && (resa.airbnbCode || resa.bookingRef)) || null
    if (!code) return // garde : non éditable sans clé stable (défense, l'UI désactive déjà)
    const mk = month
    // GARDES MOIS GELÉ (alignées sur saveMontantOverride) : règle Dimitri
    // « corrections AVANT émission ». Plancher + commission P proprio OU facture
    // voyageur V de la résa. Le ménage modifie le net proprio → même protection.
    if (!canEditMonth(mk)) {
      alert("Édition impossible : mois ≤ mars 2026 verrouillé (factures émises).")
      return
    }
    {
      const log = getLogById(lid)
      const monthLabel = monthKeyToLabel(mk)
      const blockedByCommission =
        log && findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel })
      const blockedByVoyageur =
        findBlockingVoyageurInvoice(invoicesRaw, { reservationId: resa && resa.id })
      if (blockedByCommission || blockedByVoyageur) {
        alert("Mois déjà facturé : ménage verrouillé.")
        return
      }
    }
    const nd = JSON.parse(JSON.stringify(data))
    if (!nd[mk]) nd[mk] = {}
    if (!nd[mk][lid]) nd[mk][lid] = { reservations: [] }
    const entry = nd[mk][lid]
    const trimmed = value == null ? '' : String(value).trim()
    if (trimmed === '') {
      // Reset → suppression de l'entrée (retour au défaut forfait).
      if (entry.menageOverrides && Object.prototype.hasOwnProperty.call(entry.menageOverrides, code)) {
        delete entry.menageOverrides[code]
        if (Object.keys(entry.menageOverrides).length === 0) delete entry.menageOverrides
      } else {
        return // rien à supprimer → pas d'écriture inutile
      }
    } else {
      const n = Number(trimmed.replace(',', '.'))
      if (!Number.isFinite(n) || n < 0) { alert("Montant ménage invalide."); return }
      if (!entry.menageOverrides) entry.menageOverrides = {}
      entry.menageOverrides[code] = Math.round(n * 100) / 100
    }
    try {
      await saveData(nd)
    } catch (e) {
      console.error('[saveMenageOverride] échec', e)
      alert("Échec de l'enregistrement du ménage : " + (e?.message || e))
    }
  }
  // L1 V2-edit-montant — override MONTANT net hôte manuel pour une résa V2
  // (logement 'v2' uniquement). MIROIR de saveMenageOverride : on écrit dans
  // `data` BRUT à data[mk][lid].montantOverrides[code], JAMAIS dans les
  // .reservations projetées (la projection relit l'override au render suivant
  // via applyMontantOverrides). `code` = clé stable (airbnbCode || bookingRef).
  // Une résa SANS code stable n'est PAS éditable (jamais de clé fallback).
  //   - value = nombre fini ≥ 0 → pose/écrase l'entrée.
  //   - value = null/'' → SUPPRIME l'entrée (retour au montant brut Beds24).
  // GARDES MOIS GELÉ : plancher canEditMonth + facture commission P proprio
  // (vrais logements) OU facture voyageur V émise (sous-loc, gel par résa).
  const saveMontantOverride = async (lid, resa, value) => {
    const code = (resa && (resa.airbnbCode || resa.bookingRef)) || null
    if (!code) return // garde : non éditable sans clé stable (l'UI désactive déjà)
    const mk = month
    // Plancher d'édition (factures émises ≤ mars 2026).
    if (!canEditMonth(mk)) {
      alert("Édition impossible : mois ≤ mars 2026 verrouillé (factures émises).")
      return
    }
    // Gel : mois facturé (commission P proprio) OU facture voyageur V de la résa.
    const log = getLogById(lid)
    const monthLabel = monthKeyToLabel(mk)
    const blockedByCommission =
      log && findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel })
    const blockedByVoyageur =
      findBlockingVoyageurInvoice(invoicesRaw, { reservationId: resa.id })
    if (blockedByCommission || blockedByVoyageur) {
      alert("Mois déjà facturé : montant verrouillé.")
      return
    }
    const nd = JSON.parse(JSON.stringify(data))
    if (!nd[mk]) nd[mk] = {}
    if (!nd[mk][lid]) nd[mk][lid] = { reservations: [] }
    const entry = nd[mk][lid]
    const trimmed = value == null ? '' : String(value).trim()
    if (trimmed === '') {
      // Reset → suppression de l'entrée (retour au montant brut Beds24).
      if (entry.montantOverrides && Object.prototype.hasOwnProperty.call(entry.montantOverrides, code)) {
        delete entry.montantOverrides[code]
        if (Object.keys(entry.montantOverrides).length === 0) delete entry.montantOverrides
      } else {
        return // rien à supprimer → pas d'écriture inutile
      }
    } else {
      const n = Number(trimmed.replace(',', '.'))
      if (!Number.isFinite(n) || n < 0) { alert("Montant invalide."); return }
      if (!entry.montantOverrides) entry.montantOverrides = {}
      entry.montantOverrides[code] = Math.round(n * 100) / 100
    }
    try {
      await saveData(nd)
    } catch (e) {
      console.error('[saveMontantOverride] échec', e)
      alert("Échec de l'enregistrement du montant : " + (e?.message || e))
    }
  }
  // L1 V2-edit-montant — routage du ✏️ (ResaModal) pour une résa V2.
  // Décision Dimitri : en V2 la modale corrige UNIQUEMENT montant + ménage, et
  // route vers les overrides (jamais data[mk][lid].reservations[] / saveResa KV,
  // sinon perdu au refresh par la projection).
  // `resa` = la résa PROJETÉE affichée (montant/menage = valeurs courantes) ;
  // `modalResult.montant`/`.menage` = chaînes BRUTES de la modale ('' = champ
  // vidé = demande de RESET au brut Beds24, surtout pas 0). On n'écrit que ce
  // qui a réellement changé ; un champ vidé supprime l'override (s'il existe).
  // GEL vérifié UNE seule fois ici (évite double-alerte + écriture partielle si
  // montant ET ménage changent sur un mois facturé) ; saveMontant/MenageOverride
  // gardent leur propre garde en défense.
  const saveResaV2 = async (lid, resa, modalResult) => {
    const mk = month
    if (!canEditMonth(mk)) {
      alert("Édition impossible : mois ≤ mars 2026 verrouillé (factures émises).")
      return
    }
    const log = getLogById(lid)
    const monthLabel = monthKeyToLabel(mk)
    const blocked =
      (log && findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel })) ||
      findBlockingVoyageurInvoice(invoicesRaw, { reservationId: resa && resa.id })
    if (blocked) {
      alert("Mois déjà facturé : correction verrouillée.")
      return
    }
    // '' (champ vidé) = reset ; sinon valeur numérique. round2 pour comparer au brut.
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100
    const isBlank = (v) => v == null || String(v).trim() === ''
    const montantReset = isBlank(modalResult.montant)
    const menageReset = isBlank(modalResult.menage)
    const curMontant = round2(resa && resa.montant)
    const curMenage = round2(resa && resa.menage)
    if (montantReset || round2(modalResult.montant) !== curMontant) {
      await saveMontantOverride(lid, resa, montantReset ? '' : modalResult.montant)
    }
    if (menageReset || round2(modalResult.menage) !== curMenage) {
      await saveMenageOverride(lid, resa, menageReset ? '' : modalResult.menage)
    }
    setResaModal(null)
  }
  // ── Lot 2 V2-delete-resa — SUPPRIMER une résa V2 (tombstone par code stable) ──
  // La résa Beds24 n'est JAMAIS modifiée : on pose un tombstone (data._tombstones)
  // que la projection réapplique à chaque render, tous les mois. La résa disparaît
  // de la facturation mais reste visible (grisée) côté admin avec « Restaurer ».
  // Gardes gel (canEditMonth + facture P proprio OU facture V voyageur) vérifiées
  // UNE fois en amont. Ne touche JAMAIS reservations[] (V2 = projeté, pas KV).
  const deleteResaV2 = async (lid, resa) => {
    const code = (resa && (resa.airbnbCode || resa.bookingRef)) || null
    if (!code) { setConfirm(null); return } // garde : pas de code stable → non supprimable (l'UI désactive déjà)
    const mk = month
    if (!canEditMonth(mk)) {
      alert("Suppression impossible : mois ≤ mars 2026 verrouillé (factures émises).")
      setConfirm(null); return
    }
    const log = getLogById(lid)
    // Fail-closed : un lid V2 sans log résolu = état incohérent → on n'écrit pas
    // (sinon le gel commission P serait silencieusement sauté — chemin argent).
    if (!log) { alert("Logement introuvable — suppression annulée."); setConfirm(null); return }
    const monthLabel = monthKeyToLabel(mk)
    const blocked =
      findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel }) ||
      findBlockingVoyageurInvoice(invoicesRaw, { reservationId: resa && resa.id })
    if (blocked) {
      alert("Mois déjà facturé : suppression verrouillée.")
      setConfirm(null); return
    }
    const nd = JSON.parse(JSON.stringify(data))
    pushStableCodeTombstone(nd, lid, resa)
    // Lot 3 (NP-2) : purge un éventuel déplacement de mois orphelin du même code
    // (une résa supprimée ne doit pas garder un override de mois fantôme).
    removeMonthOverride(nd, lid, code)
    try {
      await saveData(nd)
    } catch (e) {
      console.error('[deleteResaV2] échec', e)
      alert("Échec de la suppression : " + (e?.message || e))
    }
    setConfirm(null)
  }
  // ── Lot 2 V2-delete-resa — RESTAURER une résa V2 supprimée ──
  // Retire le tombstone (la projection la fera réapparaître) ET purge l'override
  // orphelin (montant/ménage) du même code stable, balayé sur TOUS les mois de
  // `data` par sécurité (en pratique le mois courant suffit). Mêmes gardes gel.
  const restoreResaV2 = async (lid, resa) => {
    const code = (resa && (resa.airbnbCode || resa.bookingRef)) || null
    if (!code) return
    const mk = month
    if (!canEditMonth(mk)) {
      alert("Restauration impossible : mois ≤ mars 2026 verrouillé (factures émises).")
      return
    }
    const log = getLogById(lid)
    if (!log) { alert("Logement introuvable — restauration annulée."); return }
    const monthLabel = monthKeyToLabel(mk)
    const blocked =
      findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel }) ||
      findBlockingVoyageurInvoice(invoicesRaw, { reservationId: resa && resa.id })
    if (blocked) {
      alert("Mois déjà facturé : restauration verrouillée.")
      return
    }
    // Relecture E : le même code vit déjà dans une autre chambre du groupe
    // (réimporté dans la bonne) → restaurer le compterait deux fois.
    const autreChambre = chambreOuLeCodeVit(data, lid, code, chambresDuGroupeBooking(lid))
    if (autreChambre) {
      alert(texteRestaurationRefusee((getLogById(autreChambre) || {}).nom || autreChambre))
      return
    }
    const nd = JSON.parse(JSON.stringify(data))
    removeStableCodeTombstone(nd, lid, resa)
    // Lot 3 (NP-2) : purge un éventuel déplacement de mois orphelin du même code
    // (une résa restaurée repart de son mois naturel, pas d'override fantôme).
    removeMonthOverride(nd, lid, code)
    // Purge override orphelin (montant + ménage) du même code, tous mois confondus.
    for (const mkKey of Object.keys(nd)) {
      if (mkKey === '_tombstones' || mkKey === '_monthOverrides') continue
      const entry = nd[mkKey] && nd[mkKey][lid]
      if (!entry || typeof entry !== 'object') continue
      if (entry.montantOverrides && Object.prototype.hasOwnProperty.call(entry.montantOverrides, code)) {
        delete entry.montantOverrides[code]
        if (Object.keys(entry.montantOverrides).length === 0) delete entry.montantOverrides
      }
      if (entry.menageOverrides && Object.prototype.hasOwnProperty.call(entry.menageOverrides, code)) {
        delete entry.menageOverrides[code]
        if (Object.keys(entry.menageOverrides).length === 0) delete entry.menageOverrides
      }
    }
    try {
      await saveData(nd)
    } catch (e) {
      console.error('[restoreResaV2] échec', e)
      alert("Échec de la restauration : " + (e?.message || e))
    }
  }
  // ── Lot 3 V2-move-resa — DÉPLACER une résa V2 d'un mois à l'autre (±1) ──
  // La résa Beds24 n'est JAMAIS modifiée : on pose data._monthOverrides[lid][code]
  // = 'YYYY-MM' (calque global, miroir _tombstones), que la projection réapplique
  // à chaque render (collectMonthOverrides + applyMonthMoves). PT-1 acté :
  // l'amplitude ±1 est validée UNIQUEMENT ICI (à la pose, depuis le NATUREL) ;
  // la projection applique l'override inconditionnellement (pas de re-validation).
  // Ne touche JAMAIS reservations[] (V2 = projeté, pas KV).
  const moveResaV2 = async (lid, resa, dir) => {
    const code = (resa && (resa.airbnbCode || resa.bookingRef)) || null
    if (!code) return // garde : pas de code stable → non déplaçable (l'UI désactive déjà)
    // XOR (sens 2) — une résa PRORATISÉE ne peut pas être déplacée (sémantiques
    // incompatibles, miroir du XOR sens 1 dans setProrataSplit). Court-circuit
    // INERTE si _prorataSplits absent (undefined → pas de refus → move inchangé).
    if (data._prorataSplits?.[lid]?.[code]) {
      alert('Cette réservation est proratisée : retirez le prorata avant de déplacer.')
      return
    }
    const naturalMk = computeInvoiceMonthByNights(resa.checkIn || resa.date, resa.checkOut)
    if (!naturalMk) { alert("Mois naturel introuvable — déplacement annulé."); return }
    const effectiveMk = (data._monthOverrides?.[lid]?.[code]) || naturalMk
    const targetMk = addMonthsMk(effectiveMk, dir)
    if (!targetMk) { alert("Mois cible introuvable — déplacement annulé."); return }
    // Amplitude ±1 MESURÉE DEPUIS LE NATUREL (comme V1) : impossible de dépasser
    // ±1 du naturel même par déplacements successifs.
    const allowed = new Set([addMonthsMk(naturalMk, -1), naturalMk, addMonthsMk(naturalMk, 1)])
    if (!allowed.has(targetMk)) {
      alert("Déplacement limité à ±1 mois autour du mois naturel.")
      return
    }
    // Plancher : cible déplaçable + les DEUX mois (naturel & cible) éditables.
    if (!canMoveTarget(targetMk)) { alert("Mois minimum mai 2026."); return }
    if (!canEditMonth(naturalMk) || !canEditMonth(targetMk)) {
      alert("Déplacement impossible : un des mois ≤ mars 2026 est verrouillé (factures émises).")
      return
    }
    // Gel + fail-closed : log requis, ET aucune des DEUX bornes (naturel/cible)
    // facturée (commission P proprio) ni la résa facturée voyageur (V). Le test
    // sur les DEUX mois est le point clé vs Lot 2 (qui n'en testait qu'un).
    const log = getLogById(lid)
    if (!log) { alert("Logement introuvable — déplacement annulé."); return }
    const blocked =
      findBlockingVoyageurInvoice(invoicesRaw, { reservationId: resa.id }) ||
      findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel: monthKeyToLabel(naturalMk) }) ||
      findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel: monthKeyToLabel(targetMk) })
    if (blocked) {
      alert("Mois déjà facturé : déplacement verrouillé.")
      return
    }
    // Anti-collision sur l'imputation EFFECTIVE du mois cible : refuser si une
    // AUTRE résa de même identité a déjà effectiveMk === targetMk pour ce lid.
    // Calcul sur les rows V2 imputées effectivement (pas sur un blob KV).
    const myKey = resaIdentityKey(resa)
    if (myKey) {
      const rows = (resasByLogement && resasByLogement.get) ? (resasByLogement.get(lid) || []) : []
      const moveMap = data._monthOverrides?.[lid] || {}
      const collision = rows.some((row) => {
        if (!row) return false
        // .trim() pour matcher la clé posée dans moveMap (= airbnbCode/bookingRef,
        // déjà trimmés par transformV2Resa) — un apiReference avec espaces sinon raterait.
        const rawRef = row.raw_payload && row.raw_payload.apiReference
        const rCode = rawRef != null ? String(rawRef).trim() || null : null
        // Ne pas comparer la résa déplacée à elle-même (même code stable).
        if (rCode && rCode === code) return false
        const rKey = resaIdentityKey({
          superhote_id: row.superhote_id,
          airbnbCode: (row.source === 'airbnb' && rCode) ? rCode : null,
          bookingRef: (row.source === 'booking' && rCode) ? rCode : null,
        })
        if (!rKey || rKey !== myKey) return false
        const rNat = computeInvoiceMonthByNights(row.start_date, row.end_date) ||
          (typeof row.start_date === 'string' ? row.start_date.slice(0, 7) : null)
        const rEff = (rCode && moveMap[rCode]) || rNat
        return rEff === targetMk
      })
      if (collision) {
        alert("Réservation identique déjà présente dans le mois cible.")
        return
      }
    }
    const nd = JSON.parse(JSON.stringify(data))
    if (targetMk === naturalMk) {
      // Retour au mois naturel → retire l'entrée (pas d'override résiduel).
      removeMonthOverride(nd, lid, code)
    } else {
      setMonthOverride(nd, lid, code, targetMk)
    }
    try {
      await saveData(nd)
    } catch (e) {
      console.error('[moveResaV2] échec', e)
      alert("Échec du déplacement : " + (e?.message || e))
    }
  }

  // ── Lot 5b V2-prorata : helper PUR de résolution de la résa NATURELLE ──
  // Réplique EXACTEMENT la source-de-vérité du moteur (data-projection.js:580-600
  // + 619-639) : on prend la résa BRUTE (rows V2 du logement), on calcule son
  // mois NATUREL (majorité de nuits), on la reprojette via getResasForMonth (même
  // fonction que la projection) puis on la retrouve par code stable. Retourne
  // { naturalResa, naturalMontant } ou null si introuvable (fail-closed).
  // ⚠️ B1 — naturalMontant = montant NET HÔTE de la résa NATURELLE (entry.resa.montant
  // côté moteur), PAS le `resa` projeté de la modale (qui peut être une PART prorata).
  const resolveNaturalResaV2 = (lid, code) => {
    if (!code) return null
    const rows = (resasByLogement && resasByLogement.get) ? (resasByLogement.get(lid) || []) : []
    if (!Array.isArray(rows) || rows.length === 0) return null
    // Mois naturels couverts par les rows de ce logement (miroir boucle moteur).
    const naturalMks = new Set()
    for (const row of rows) {
      if (!row) continue
      const ci = row.start_date || null
      const co = row.end_date || null
      const nat = computeInvoiceMonthByNights(ci, co) || (typeof ci === 'string' ? ci.slice(0, 7) : null)
      if (nat) naturalMks.add(nat)
    }
    for (const natMk of naturalMks) {
      const { resas: natResas } = getResasForMonth(rows, natMk, {})
      for (const r of natResas) {
        if (!r) continue
        const rCode = r.airbnbCode || r.bookingRef || null
        if (rCode && String(rCode) === String(code)) {
          return { naturalResa: r, naturalMontant: Number(r.montant) }
        }
      }
    }
    return null
  }

  // ── Lot 5b V2-prorata : POSER le calque prorata (data._prorataSplits[lid][code]) ──
  // Calqué sur moveResaV2. Garde-fous délégués à assertProrataPosable (gel série
  // P+V, XOR move, Σ manual contre le total POST-OVERRIDE / PRÉ-PRORATA — B1).
  const setProrataSplit = async (lid, resa, spec) => {
    const code = (resa && (resa.airbnbCode || resa.bookingRef)) || null
    if (!code) { alert('Réservation non proratisable (pas de code stable).'); return }
    const log = getLogById(lid)
    if (!log) { alert('Logement introuvable — proratisation annulée.'); return }
    // B1 — TOTAL POST-OVERRIDE / PRÉ-PRORATA : on part de la résa NATURELLE (pas
    // du `resa` projeté de la modale), puis on applique l'override montant s'il
    // existe (collectMontantOverrides), comme le moteur (data-projection.js:633).
    const nat = resolveNaturalResaV2(lid, code)
    if (!nat || !Number.isFinite(nat.naturalMontant)) {
      alert('Montant naturel introuvable — proratisation annulée.')
      return
    }
    const overrides = collectMontantOverrides(data, v2Lids)
    const ov = overrides[lid] && overrides[lid][code]
    const montantTotal = Number.isFinite(Number(ov)) ? Math.round(Number(ov) * 100) / 100 : nat.naturalMontant
    if (!Number.isFinite(montantTotal)) {
      alert('Montant total indéterminable — proratisation annulée.')
      return
    }
    // Mois touchés : DATE-BOUNDED (tout le séjour, indépendant de la fenêtre) —
    // MÊME source que le moteur (data-projection.js). Fenêtre plus un intrant.
    const monthsTouched = computeMonthsTouched(nat.naturalResa)
    const res = assertProrataPosable({
      resa: nat.naturalResa,
      code,
      monthsTouched,
      montantTotal,
      spec,
      invoicesRaw,
      logId: log.id,
      monthKeyToLabel,
      existingMonthOverride: data._monthOverrides?.[lid]?.[code],
    })
    if (!res.ok) { alert(res.message); return }
    const nd = JSON.parse(JSON.stringify(data))
    setProrataSplitStore(nd, lid, code, spec)
    try {
      await saveData(nd)
    } catch (e) {
      console.error('[setProrataSplit] échec', e)
      alert('Échec de la proratisation : ' + (e?.message || e))
    }
  }

  // ── Lot 5b V2-prorata : RETIRER le calque prorata (retour mois naturel) ──
  // AMENDEMENT M3 — garde DÉCOCHAGE multi-mois : un mois touché (ou la résa série
  // V) déjà facturé NE PEUT PAS reperdre sa part → on refuse le décochage.
  const clearProrataSplit = async (lid, resa) => {
    const code = (resa && (resa.airbnbCode || resa.bookingRef)) || null
    if (!code) return // pas de calque possible sans code (no-op silencieux)
    const log = getLogById(lid)
    if (!log) { alert('Logement introuvable — retrait du prorata annulé.'); return }
    const nat = resolveNaturalResaV2(lid, code)
    // Si la résa naturelle est introuvable (tombstonée/disparue), on autorise le
    // retrait du calque résiduel (rien à geler côté projection).
    if (nat && nat.naturalResa) {
      const monthsTouched = computeMonthsTouched(nat.naturalResa)
      // GEL (mêmes 3 findBlocking* que la POSE) : un mois figé ne peut pas reperdre sa part.
      let blocked = false
      if (Array.isArray(invoicesRaw)) {
        if (nat.naturalResa.id != null && findBlockingVoyageurInvoice(invoicesRaw, { reservationId: nat.naturalResa.id })) {
          blocked = true
        }
        for (const mk of monthsTouched) {
          const label = monthKeyToLabel(mk)
          if (!label) continue
          if (
            findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel: label }) ||
            findBlockingVoyageurInvoiceForMonth(invoicesRaw, { logId: log.id, monthLabel: label })
          ) {
            blocked = true
            break
          }
        }
      }
      if (blocked) {
        alert('Un mois concerné est déjà facturé : retrait du prorata verrouillé.')
        return
      }
    }
    const nd = JSON.parse(JSON.stringify(data))
    removeProrataSplitStore(nd, lid, code)
    try {
      await saveData(nd)
    } catch (e) {
      console.error('[clearProrataSplit] échec', e)
      alert('Échec du retrait du prorata : ' + (e?.message || e))
    }
  }

  // ── Lot 4 V2-create-manual — CRÉER une résa MANUELLE sur un logement V2 ──
  // Décision Dimitri : on réutilise le bouton « + Résa » + la modale V1, on
  // ROUTE le onSave ici quand le logement est V2. La manuelle est un élément KV
  // POSÉ dans data[month][lid].reservations (bucket physique = mois courant, pas
  // de month_override) avec source:'manual' + plateforme:'manuel'. La projection
  // la FUSIONNE aux résas Beds24 (collectManualResasForMonth). Geste calqué sur
  // saveResa (création), mais avec gel par MOIS (la résa n'a pas d'id pour gel
  // par résa) et plateforme forcée.
  const createResaV2 = (lid, resa) => {
    // Plancher : pas de création ≤ avril 2026 (factures émises).
    if (!canCreateMonth(month)) {
      alert("Création impossible : mois minimum mai 2026 (plancher facturation).")
      return
    }
    // Fail-closed : un lid V2 sans log résolu = état incohérent → on n'écrit pas
    // (sinon le gel commission P/voyageur V serait silencieusement sauté).
    const log = getLogById(lid)
    if (!log) { alert("Logement introuvable — création annulée."); return }
    const monthLabel = monthKeyToLabel(month)
    // GEL par MOIS : commission P proprio (vrais logements) OU facture V voyageur
    // (sous-loc Maison Zen) déjà émise sur ce logement/mois → mois figé.
    const blocked =
      findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel }) ||
      findBlockingVoyageurInvoiceForMonth(invoicesRaw, { logId: log.id, monthLabel })
    if (blocked) {
      alert("Mois déjà facturé : création verrouillée.")
      return
    }
    const nd = JSON.parse(JSON.stringify(data))
    ensureEntry(nd, lid)
    // applyManualOriginFlag pose manual_origin/préserve ical_uid (comme V1) ; on
    // force EN PLUS source:'manual' (discriminant fusion) + plateforme:'manuel'
    // (défense, même si la modale verrouille déjà la plateforme). id neuf.
    // Estampage ORIGINE additif (Lot 0 Statistiques proprio) : une manuelle ne
    // passe PAS par transformV2Resa → on pose ici les champs origine pour que
    // l'identité ca_brut_origine − montant − taxe === ota_commission(0) tienne.
    // Une manuelle n'a pas d'OTA : brut = net + taxe, commission OTA = 0.
    const _r2 = (n) => Math.round((Number(n) || 0) * 100) / 100
    const manualMontant = _r2(resa && resa.montant)
    const manualTaxe = _r2(resa && resa.taxe)
    nd[month][lid].reservations.push({
      ...applyManualOriginFlag(resa, null),
      source: 'manual',
      plateforme: 'manuel',
      id: uid(),
      ca_brut_origine: _r2(manualMontant + manualTaxe),
      ota_commission: 0,
      montant_origine: manualMontant,
    })
    saveData(nd); setResaModal(null)
  }
  // ── Lot 4 V2-create-manual — SUPPRIMER une résa MANUELLE (handler DÉDIÉ) ──
  // NE TOUCHE PAS deleteResa V1 (partagé l1-l7). Une manuelle vit dans son
  // bucket physique (pas forcément le mois affiché si on a changé de mois) →
  // findResaBucket. SANS pushTombstonesForResa : une manuelle n'a pas de code ;
  // un tombstone legacy_checkin masquerait une future résa Beds24 de même date.
  const deleteManualResa = (lid, resaId) => {
    const nd = JSON.parse(JSON.stringify(data))
    const loc = findResaBucket(nd, lid, resaId)
    if (!loc) { setConfirm(null); return } // déjà supprimée → pas de save no-op
    nd[loc.mk][lid].reservations.splice(loc.idx, 1)
    saveData(nd); setConfirm(null)
  }
  // Plan B — déplacement physique d'une résa entre buckets mensuels (±1 mois
  // adjacent au mois naturel) + pose/suppression de month_override.
  const moveResa = (lid, resaId, dir) => {
    const res = moveResaMonth(data, lid, resaId, dir)
    if (res.ok) { saveData(res.data); return }
    const msgs = {
      floor: "Mois minimum mai 2026.",
      amplitude: "Déplacement limité à ±1 mois autour du mois naturel.",
      collision: "Réservation identique déjà présente dans le mois cible.",
      not_found: "Réservation introuvable.",
    }
    alert(msgs[res.reason] || "Déplacement impossible.")
  }
  // Helper UI ◀▶ : un move dans une direction est-il autorisé
  // (ré-ancrable + amplitude ±1 mois naturel + plancher canMoveTarget) ?
  const canMoveDir = (lid, r, dir) => {
    if (!isRelocatable(r)) return false
    const loc = findResaBucket(data, lid, r.id)
    if (!loc) return false
    const targetMk = addMonthsMk(loc.mk, dir)
    if (!targetMk) return false
    const naturalMk = computeInvoiceMonthByNights(r.checkIn || r.date, r.checkOut)
    const allowed = new Set([addMonthsMk(naturalMk, -1), naturalMk, addMonthsMk(naturalMk, 1)])
    if (!allowed.has(targetMk)) return false
    if (!canMoveTarget(targetMk)) return false
    return true
  }
  return {
    hasData, countIncompletesLog, countIncompletesLogWithChambres, navM, ensureEntry, saveResa,
    saveResaV2, deleteResaV2, restoreResaV2, moveResaV2, resolveNaturalResaV2, setProrataSplit,
    clearProrataSplit, createResaV2, deleteManualResa, moveResa, canMoveDir,
  }
}
